import Client from './clients/AuthenticatedClient';

/**
 * ProfileRepository — HTTP access for `/profile` (per-user overlay store:
 * market role, role presets, products, applied-preset history).
 *
 * Endpoints:
 *   GET   /profile/me            → Profile
 *   PATCH /profile/me            → Profile
 *   GET   /profile/role-presets  → { items: RolePreset[] }
 *   POST  /profile/apply-preset  → ApplyPresetResult
 *   GET   /profile/products      → { items: Product[] }
 *
 * Conventions:
 * - Uses the shared `AuthenticatedClient`; its baseURL already includes the
 *   `/api/v2` prefix.
 * - Methods return the raw axios response; the service unwraps `.data` and runs
 *   its own state-mirroring / defaulting logic.
 */
const PROFILE_PATH = '/profile/me';
const PRESETS_PATH = '/profile/role-presets';
const APPLY_PRESET_PATH = '/profile/apply-preset';
const PRODUCTS_PATH = '/profile/products';

export default {
  /**
   * Fetch the current user's profile.
   * @returns {Promise<import('axios').AxiosResponse>} raw response; `.data` is the profile.
   */
  getProfile() {
    return Client.get(PROFILE_PATH);
  },

  /**
   * Patch the profile. Caller is responsible for building the partial payload.
   * @param {Object} payload - only the keys to update.
   * @returns {Promise<import('axios').AxiosResponse>} raw response; `.data` is the updated profile.
   */
  patchProfile(payload) {
    return Client.patch(PROFILE_PATH, payload);
  },

  /**
   * List the available role presets.
   * @returns {Promise<import('axios').AxiosResponse>} raw response; `.data.items` is the array.
   */
  getRolePresets() {
    return Client.get(PRESETS_PATH);
  },

  /**
   * Apply a role preset.
   * @param {string} role
   * @returns {Promise<import('axios').AxiosResponse>} raw response; `.data` is the change-set.
   */
  applyPreset(role) {
    return Client.post(APPLY_PRESET_PATH, { role });
  },

  /**
   * List the user's subscription products.
   * @returns {Promise<import('axios').AxiosResponse>} raw response; `.data.items` is the array.
   */
  getProducts() {
    return Client.get(PRODUCTS_PATH);
  },
};
